import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, CreditCard, XCircle, ExternalLink, ArrowUpRight, Loader2 } from "lucide-react";

/**
 * Settings → Plan & Billing. Reads from /api/billing/my-subscription
 * (server-side entitlement source) and hands off to Paddle-hosted URLs
 * for update-card and cancel actions.
 */
export default function BillingPlan() {
    const [state, setState] = useState({ loading: true });
    const [mgmtBusy, setMgmtBusy] = useState(false);

    useEffect(() => {
        api.get("/billing/my-subscription")
            .then((r) => setState({ loading: false, ...r.data }))
            .catch(() => setState({ loading: false, has_subscription: false }));
    }, []);

    const openMgmt = async (key) => {
        setMgmtBusy(true);
        try {
            const r = await api.get("/billing/manage-urls");
            const url = r.data?.[key];
            if (!url) { toast.error("Paddle management URL not available. Contact billing support."); return; }
            window.open(url, "_blank", "noopener");
        } catch (e) {
            toast.error("Could not fetch a management link. Please try again shortly.");
        } finally { setMgmtBusy(false); }
    };

    if (state.loading) {
        return <div className="max-w-3xl mx-auto p-8 flex items-center gap-2 text-sm text-stone-500"><Loader2 size={14} className="animate-spin" /> Loading your plan…</div>;
    }

    if (!state.has_subscription) {
        return (
            <div className="max-w-3xl mx-auto p-6 md:p-8" data-testid="billing-no-sub">
                <Breadcrumb />
                <h1 className="text-2xl font-bold tracking-tight">Plan &amp; Billing</h1>
                <p className="text-sm text-stone-500 mt-1">You&apos;re currently on the free tier or trial. Pick a paid plan whenever you&apos;re ready.</p>
                <Link to="/pricing" className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-stone-900 text-white px-5 py-2 text-sm font-semibold hover:bg-stone-800">View pricing plans <ArrowUpRight size={13} /></Link>
            </div>
        );
    }

    const sub = state.subscription || {};
    const ent = state.entitlements || {};

    return (
        <div className="max-w-3xl mx-auto p-6 md:p-8" data-testid="billing-plan">
            <Breadcrumb />
            <h1 className="text-2xl font-bold tracking-tight">Plan &amp; Billing</h1>
            <p className="text-sm text-stone-500 mt-1">Manage your VAULT subscription. Payment updates and cancellation open on Paddle&apos;s hosted portal.</p>

            {ent.banner && (
                <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm px-3 py-2" data-testid="billing-banner">{ent.banner}</div>
            )}

            <section className="mt-6 rounded-xl border border-stone-200 bg-white p-5">
                <div className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3">Current subscription</div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <Field k="Plan" v={sub.internal_plan_slug} />
                    <Field k="Status" v={sub.status} testid="billing-status" />
                    <Field k="Billing frequency" v={sub.billing_interval || "—"} />
                    <Field k="Currency" v={sub.billing_currency || "—"} />
                    <Field k="Next billed at" v={sub.next_billed_at ? fmtDate(sub.next_billed_at) : "—"} testid="billing-next-billed" />
                    <Field k="Trial ends" v={sub.trial_end ? fmtDate(sub.trial_end) : "—"} />
                    <Field k="Payment method" v={sub.card_brand && sub.card_last4 ? `${sub.card_brand.toUpperCase()} •••• ${sub.card_last4}` : "—"} />
                    <Field k="Provider" v="Paddle" />
                </dl>
            </section>

            <section className="mt-4 grid sm:grid-cols-2 gap-4">
                <button
                    onClick={() => openMgmt("update_payment_method")}
                    disabled={mgmtBusy}
                    data-testid="billing-update-card"
                    className="rounded-xl border border-stone-200 bg-white p-5 text-left hover:border-amber-400 transition"
                >
                    <div className="flex items-center gap-2 text-sm font-semibold"><CreditCard size={13} /> Update payment method <ExternalLink size={11} className="text-stone-400" /></div>
                    <div className="text-xs text-stone-500 mt-1">Opens Paddle&apos;s hosted portal — VAULT never sees your card details.</div>
                </button>
                <button
                    onClick={() => openMgmt("cancel")}
                    disabled={mgmtBusy}
                    data-testid="billing-cancel"
                    className="rounded-xl border border-stone-200 bg-white p-5 text-left hover:border-rose-400 transition"
                >
                    <div className="flex items-center gap-2 text-sm font-semibold"><XCircle size={13} /> Cancel subscription <ExternalLink size={11} className="text-stone-400" /></div>
                    <div className="text-xs text-stone-500 mt-1">Cancel at period end. Your data stays intact per our retention policy.</div>
                </button>
                <Link to="/billing-terms" className="rounded-xl border border-stone-200 bg-white p-5 hover:border-amber-400 transition">
                    <div className="text-sm font-semibold">Subscription &amp; Billing Terms</div>
                    <div className="text-xs text-stone-500 mt-1">Auto-renewal, taxes, upgrades, downgrades and cancellation windows.</div>
                </Link>
                <Link to="/refund-policy" className="rounded-xl border border-stone-200 bg-white p-5 hover:border-amber-400 transition">
                    <div className="text-sm font-semibold">Refund &amp; Cancellation Policy</div>
                    <div className="text-xs text-stone-500 mt-1">Eligibility, timings, refund method and disputes.</div>
                </Link>
            </section>
        </div>
    );
}

function Breadcrumb() {
    return (
        <div className="mb-4 flex items-center gap-2 text-xs">
            <Link to="/dashboard" className="text-stone-500 hover:text-stone-900 flex items-center gap-1"><ArrowLeft size={12} /> Dashboard</Link>
            <span className="text-stone-300">/</span>
            <span className="text-stone-500">Settings</span>
            <span className="text-stone-300">/</span>
            <span className="font-medium">Plan &amp; Billing</span>
        </div>
    );
}
function Field({ k, v, testid }) {
    return (
        <div>
            <dt className="text-xs text-stone-500">{k}</dt>
            <dd className="font-medium" data-testid={testid}>{v || "—"}</dd>
        </div>
    );
}
